<template>
  <!-- Photo View Modal -->
  <div class="modal fade" id="photoViewModal" tabindex="-1" aria-labelledby="photoViewModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content bg-dark">
        <!-- Modal Header -->
        <div class="modal-header bg-dark text-white border-secondary">
          <h5 class="modal-title d-flex align-items-center" id="photoViewModalLabel">
            <i class="bx bx-image me-2"></i>
            {{ modalTitle }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal" aria-label="Close"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body bg-dark text-center p-0" style="min-height: 400px;">
          <!-- Loading State -->
          <div v-if="loading" class="d-flex flex-column justify-content-center align-items-center h-100" style="min-height: 400px;">
            <div class="spinner-border text-light mb-3" role="status">
              <span class="visually-hidden">Loading photo...</span>
            </div>
            <p class="text-light">Loading photo...</p>
          </div>

          <!-- Photo Display -->
          <div v-else-if="photoUrl" class="position-relative photo-container">
            <img
                :src="photoUrl"
                :alt="`${displayName} photo`"
                class="img-fluid photo-display"
                style="max-height: 70vh; width: auto; object-fit: contain;"
                @load="onPhotoLoad"
                @error="onPhotoError"
            />

            <!-- Photo Info Overlay -->
            <div class="photo-info-overlay position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-75 text-white p-3">
              <div class="row align-items-center">
                <div class="col-md-8 text-start">
                  <div class="d-flex flex-column flex-md-row gap-2">
                    <span class="badge bg-primary">
                      <i class="bx bx-user me-1"></i>
                      {{ displayName }}
                    </span>
                    <span v-if="entityType" class="badge bg-info">
                      <i :class="getEntityIcon()" class="me-1"></i>
                      {{ entityTypeLabel }}
                    </span>
                    <span v-if="uploadDate" class="badge bg-secondary">
                      <i class="bx bx-calendar me-1"></i>
                      Uploaded: {{ formatDate(uploadDate) }}
                    </span>
                    <span v-if="uploadedBy" class="badge bg-secondary">
                      <i class="bx bx-user-check me-1"></i>
                      By: {{ uploadedBy }}
                    </span>
                  </div>
                  <div v-if="additionalInfo" class="mt-2">
                    <small class="text-light">{{ additionalInfo }}</small>
                  </div>
                </div>
                <div class="col-md-4 text-end mt-2 mt-md-0">
                  <div class="btn-group" role="group">
                    <!-- Zoom Controls -->
                    <button class="btn btn-outline-light btn-sm" @click="zoomOut" :disabled="zoomLevel <= 0.5">
                      <i class="bx bx-zoom-out"></i>
                    </button>
                    <button class="btn btn-outline-light btn-sm" @click="resetZoom">
                      <i class="bx bx-refresh"></i>
                    </button>
                    <button class="btn btn-outline-light btn-sm" @click="zoomIn" :disabled="zoomLevel >= 3">
                      <i class="bx bx-zoom-in"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Photo State -->
          <div v-else class="d-flex flex-column justify-content-center align-items-center h-100 text-light" style="min-height: 400px;">
            <i class="bx bx-image-alt display-1 text-muted mb-3"></i>
            <h5 class="text-muted">No Photo Available</h5>
            <p class="text-muted">{{ noPhotoMessage }}</p>
          </div>

          <!-- Error State -->
          <div v-if="error" class="position-absolute top-0 start-0 end-0">
            <div class="alert alert-danger m-3" role="alert">
              <i class="bx bx-error-circle me-2"></i>
              {{ error }}
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-dark border-secondary">
          <div class="d-flex justify-content-between w-100">
            <div class="d-flex gap-2">
              <!-- Photo Actions -->
              <button v-if="photoUrl"
                      class="btn btn-success btn-sm"
                      @click="downloadPhoto"
                      :disabled="downloading">
                <span v-if="downloading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-else class="bx bx-download me-2"></i>
                {{ downloading ? 'Downloading...' : 'Download Photo' }}
              </button>

              <button v-if="photoUrl"
                      class="btn btn-info btn-sm"
                      @click="copyPhotoUrl"
                      :disabled="copying">
                <i class="bx bx-copy me-2"></i>
                {{ copying ? 'Copied!' : 'Copy URL' }}
              </button>

              <button v-if="photoUrl"
                      class="btn btn-warning btn-sm"
                      @click="openInNewTab">
                <i class="bx bx-link-external me-2"></i>
                Open in New Tab
              </button>
            </div>

            <div class="d-flex gap-2">
              <!-- View Controls -->
              <button v-if="photoUrl"
                      class="btn btn-outline-light btn-sm"
                      @click="openFullscreen">
                <i class="bx bx-fullscreen me-2"></i>
                Fullscreen
              </button>

              <button class="btn btn-secondary" @click="closeModal">
                <i class="bx bx-x me-2"></i>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'PhotoViewModal',
  props: {
    memberInfo: {
      type: Object,
      default: () => ({})
    },
    photoInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      downloading: false,
      copying: false,
      error: null,
      zoomLevel: 1,
      photoUrl: null,

      // Entity information
      entityType: 'member', // 'member' or 'beneficiary'
      entityData: null,
      displayName: '',
      modalTitle: 'View Photo',
      additionalInfo: '',
      noPhotoMessage: 'No photo available',
      downloadFilename: 'photo',
      uploadDate: null,
      uploadedBy: null
    };
  },
  computed: {
    entityTypeLabel() {
      if (this.entityType === 'beneficiary' && this.entityData) {
        const relationship = this.entityData.relationship || 'Beneficiary';
        return this.capitalizeFirst(relationship);
      }
      return 'Primary Member';
    }
  },
  methods: {
    // Open modal for member photo
    openModal(photoUrl = null) {
      this.resetState();
      this.entityType = 'member';
      this.entityData = this.memberInfo;
      this.displayName = this.memberInfo?.fullName || 'Unknown Member';
      this.modalTitle = `Member Photo - ${this.displayName}`;
      this.additionalInfo = `Member ID: ${this.memberInfo?.cbhiNumber || 'N/A'}`;
      this.noPhotoMessage = "This member doesn't have a profile photo uploaded.";
      this.downloadFilename = `member_${this.memberInfo?.cbhiNumber || 'photo'}`;

      // Set upload info
      this.uploadDate = this.photoInfo?.uploadDate || this.memberInfo?.photo_upload_date;
      this.uploadedBy = this.photoInfo?.uploadedByUsername || this.memberInfo?.photo_uploaded_by_username;

      this.photoUrl = photoUrl || this.getPhotoUrl();
      this.showModal();
    },

    // Open modal for beneficiary photo
    openModalForBeneficiary(beneficiary, photoUrl) {
      this.resetState();
      this.entityType = 'beneficiary';
      this.entityData = beneficiary;

      const fullName = this.getBeneficiaryFullName(beneficiary);
      this.displayName = fullName;
      this.modalTitle = `Beneficiary Photo - ${fullName}`;

      const age = this.calculateAge(beneficiary.date_of_birth);
      const gender = this.capitalizeFirst(beneficiary.gender || '');
      const relationship = this.capitalizeFirst(beneficiary.relationship || '');

      this.additionalInfo = `${relationship} • ${age} years old ${gender}`;
      if (beneficiary.id_number) {
        this.additionalInfo += ` • ID: ${beneficiary.id_number}`;
      }

      this.noPhotoMessage = "This beneficiary doesn't have a profile photo uploaded.";
      this.downloadFilename = `beneficiary_${beneficiary.beneficiary_id}_photo`;

      // Set upload info for beneficiary
      this.uploadDate = beneficiary.photo_upload_date;
      this.uploadedBy = beneficiary.photo_uploaded_by_username;

      this.photoUrl = photoUrl;
      this.showModal();
    },

    // Show modal
    showModal() {
      this.loading = true;
      const modal = new bootstrap.Modal(document.getElementById('photoViewModal'));
      modal.show();

      if (this.photoUrl) {
        setTimeout(() => {
          this.loading = false;
        }, 500);
      } else {
        this.loading = false;
      }
    },

    // Get entity icon based on type
    getEntityIcon() {
      if (this.entityType === 'beneficiary') {
        const relationship = this.entityData?.relationship?.toLowerCase() || '';
        if (relationship.includes('spouse')) return 'bx bx-heart';
        if (relationship.includes('child') || relationship.includes('son') || relationship.includes('daughter')) return 'bx bx-child';
        if (relationship.includes('parent') || relationship.includes('mother') || relationship.includes('father')) return 'bx bx-home-heart';
        return 'bx bx-group';
      }
      return 'bx bx-user-pin';
    },

    // Get beneficiary full name
    getBeneficiaryFullName(beneficiary) {
      return `${beneficiary.first_name} ${beneficiary.middle_name || ''} ${beneficiary.surname || ''}`.trim();
    },

    // Calculate age
    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const dob = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      return age;
    },

    // Capitalize first letter
    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },

    // Close modal
    closeModal() {
      const modal = bootstrap.Modal.getInstance(document.getElementById('photoViewModal'));
      if (modal) {
        modal.hide();
      }
      this.resetState();
    },

    // Reset component state
    resetState() {
      this.loading = false;
      this.downloading = false;
      this.copying = false;
      this.error = null;
      this.zoomLevel = 1;
      this.photoUrl = null;
      this.entityType = 'member';
      this.entityData = null;
      this.displayName = '';
      this.modalTitle = 'View Photo';
      this.additionalInfo = '';
      this.uploadDate = null;
      this.uploadedBy = null;
    },

    // Get photo URL from props
    getPhotoUrl() {
      if (this.photoInfo?.photoUrl) return this.photoInfo.photoUrl;
      if (this.photoInfo?.photoPath) {
        if (this.photoInfo.photoPath.startsWith('http')) {
          return this.photoInfo.photoPath;
        }
        return `https://api.paysol.co.ke/cbhi/uploads/member-photos/${this.photoInfo.photoPath}`;
      }
      if (this.memberInfo?.profile_photo) {
        if (this.memberInfo.profile_photo.startsWith('http')) {
          return this.memberInfo.profile_photo;
        }
        return `https://api.paysol.co.ke/cbhi/uploads/member-photos/${this.memberInfo.profile_photo}`;
      }
      return null;
    },

    // Handle photo load success
    onPhotoLoad() {
      this.loading = false;
      this.error = null;
    },

    // Handle photo load error
    onPhotoError() {
      this.loading = false;
      this.error = 'Failed to load photo. The image may be corrupted or unavailable.';
    },

    // Download photo
    async downloadPhoto() {
      if (!this.photoUrl) return;

      this.downloading = true;
      this.error = null;

      try {
        const timestamp = new Date().toISOString().slice(0, 10);
        const filename = `${this.downloadFilename}_${timestamp}.jpg`;

        const response = await fetch(this.photoUrl);
        if (!response.ok) throw new Error('Failed to download photo');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$emit('photo-downloaded', {
          success: true,
          filename: filename,
          entityType: this.entityType,
          entityName: this.displayName
        });

      } catch (error) {
        console.error('Download error:', error);
        this.error = 'Failed to download photo. Please try again.';
        this.$emit('download-error', { error: error.message });
      } finally {
        this.downloading = false;
      }
    },

    // Copy photo URL to clipboard
    async copyPhotoUrl() {
      if (!this.photoUrl) return;
      this.copying = true;

      try {
        await navigator.clipboard.writeText(this.photoUrl);
        setTimeout(() => {
          this.copying = false;
        }, 1500);

        this.$emit('url-copied', {
          success: true,
          url: this.photoUrl,
          entityType: this.entityType
        });

      } catch (error) {
        console.error('Copy error:', error);
        this.copying = false;
        this.error = 'Failed to copy URL to clipboard.';
        this.$emit('copy-error', { error: error.message });
      }
    },

    // Open in new tab
    openInNewTab() {
      if (this.photoUrl) {
        window.open(this.photoUrl, '_blank');
      }
    },

    // Zoom controls
    zoomIn() {
      if (this.zoomLevel < 3) {
        this.zoomLevel += 0.25;
        this.applyZoom();
      }
    },

    zoomOut() {
      if (this.zoomLevel > 0.5) {
        this.zoomLevel -= 0.25;
        this.applyZoom();
      }
    },

    resetZoom() {
      this.zoomLevel = 1;
      this.applyZoom();
    },

    applyZoom() {
      const img = document.querySelector('.photo-display');
      if (img) {
        img.style.transform = `scale(${this.zoomLevel})`;
        img.style.transformOrigin = 'center';
        img.style.transition = 'transform 0.3s ease';
      }
    },

    // Open in fullscreen
    openFullscreen() {
      const img = document.querySelector('.photo-display');
      if (img?.requestFullscreen) {
        img.requestFullscreen();
      } else if (img?.mozRequestFullScreen) {
        img.mozRequestFullScreen();
      } else if (img?.webkitRequestFullscreen) {
        img.webkitRequestFullscreen();
      } else if (img?.msRequestFullscreen) {
        img.msRequestFullscreen();
      }
    },

    // Format date
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  },

  mounted() {
    // Handle fullscreen exit
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement) {
        this.resetZoom();
      }
    });
  }
};
</script>

<style scoped>
/* Keep all your existing styles - they're perfect! */
/* Modal Enhancements */
.modal-dialog.modal-lg {
  max-width: 90vw;
}

.modal-content.bg-dark {
  background-color: #1a1a1a !important;
  border: 1px solid #444;
}

.modal-header.bg-dark {
  background-color: #2a2a2a !important;
  border-bottom: 1px solid #444;
}

.modal-footer.bg-dark {
  background-color: #2a2a2a !important;
  border-top: 1px solid #444;
}

/* Photo Container */
.photo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  overflow: hidden;
}

.photo-display {
  max-height: 70vh;
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  cursor: zoom-in;
  transition: transform 0.3s ease;
}

.photo-display:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
}

/* Photo Info Overlay */
.photo-info-overlay {
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%) !important;
  border-radius: 0 0 8px 8px;
  backdrop-filter: blur(2px);
}

/* Loading State */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Badges */
.badge {
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
}

/* Buttons */
.btn {
  border-radius: 0.375rem;
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Button Group */
.btn-group .btn {
  margin: 0;
}

.btn-group .btn:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.btn-group .btn:not(:first-child) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-dialog.modal-lg {
    max-width: 95vw;
    margin: 1rem;
  }

  .photo-display {
    max-height: 50vh;
  }

  .photo-info-overlay {
    padding: 1rem;
  }

  .modal-footer .d-flex {
    flex-direction: column;
    gap: 0.5rem;
  }
}

/* Fullscreen Styles */
.photo-display:fullscreen {
  width: 100vw;
  height: 100vh;
  max-width: none;
  max-height: none;
  object-fit: contain;
  background: black;
}
</style>